import React from 'react';
import type { VisitsEntriesResponse } from '@troia-v3/types';
import { getLeadChannelLabel } from '@troia-v3/types';

import { DashCard, formatNumber } from '@/shared/components/ui/dashboard';
import { ListPagePagination } from '@/shared/components/ui';

interface Props {
  data: VisitsEntriesResponse | undefined;
  isLoading: boolean;
  page: number;
  onPageChange: (page: number) => void;
}

const formatWindow = (startedAt: string, endedAt: string): string => {
  const start = new Date(startedAt);
  const end = new Date(endedAt);
  const date = start.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  const from = start.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  // Hit pontual tem duração zero — mostrar "14:00 – 14:00" seria ruído.
  if (start.getTime() === end.getTime()) return `${date} ${from}`;
  const to = end.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  return `${date} ${from}–${to}`;
};

/**
 * Extrato cru. É a válvula de escape quando um gráfico parece errado: dá para
 * conferir janela por janela contra o painel do fornecedor.
 */
export const VisitsEntriesCard: React.FC<Props> = ({ data, isLoading, page, onPageChange }) => (
  <DashCard
    icon="list"
    title="Extrato de visitas"
    subtitle={data ? `${formatNumber(data.total)} registro(s)` : undefined}
    testId="analytics-visits-entries-card"
    noPadding
  >
    <div className="overflow-x-auto">
      <table className="w-full text-sm" style={{ minWidth: 640 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
            {['Janela', 'Unidade', 'Canal', 'Origem', 'Visitas'].map((h, i) => (
              <th
                key={h}
                className={`px-4 py-2.5 text-xs font-semibold ${i === 4 ? 'text-right' : 'text-left'}`}
                style={{ color: 'var(--theme-muted-text)' }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {isLoading && (
            <tr>
              <td colSpan={5} className="px-4 py-8 text-center" style={{ color: 'var(--theme-muted-text)' }}>
                Carregando…
              </td>
            </tr>
          )}
          {!isLoading && (data?.items.length ?? 0) === 0 && (
            <tr>
              <td colSpan={5} className="px-4 py-8 text-center" style={{ color: 'var(--theme-muted-text)' }}>
                Nenhuma visita no período.
              </td>
            </tr>
          )}
          {data?.items.map((row) => (
            <tr
              key={row.id}
              style={{ borderBottom: '1px solid var(--theme-card-border)' }}
              data-testid={`analytics-visits-entries-row-${row.id}`}
            >
              <td className="px-4 py-2.5 whitespace-nowrap" style={{ color: 'var(--theme-card-text)' }}>
                {formatWindow(row.startedAt, row.endedAt)}
              </td>
              <td className="px-4 py-2.5" style={{ color: 'var(--theme-muted-text)' }}>
                {row.unitName ?? '—'}
              </td>
              <td className="px-4 py-2.5" style={{ color: 'var(--theme-muted-text)' }}>
                {getLeadChannelLabel(row.channel)}
              </td>
              <td className="px-4 py-2.5 truncate max-w-[220px]" style={{ color: 'var(--theme-muted-text)' }}>
                {row.origin}
              </td>
              <td
                className="px-4 py-2.5 text-right font-semibold whitespace-nowrap"
                style={{ color: 'var(--theme-card-text)' }}
              >
                {formatNumber(row.views)}
                {row.occurrences > 1 && (
                  // A fonte mandou mais de uma linha para esta mesma janela.
                  // Marcar deixa a ambiguidade visível em vez de escondida.
                  <span
                    className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full align-middle"
                    style={{
                      backgroundColor: 'var(--theme-warning-subtle)',
                      color: 'var(--theme-warning-fg)',
                    }}
                    title={`A fonte enviou ${row.occurrences} leituras para esta janela; os valores foram somados.`}
                    data-testid={`analytics-visits-entries-ambiguous-${row.id}`}
                  >
                    {row.occurrences}×
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    {data && data.total > data.limit && (
      <div className="px-4 py-3" style={{ borderTop: '1px solid var(--theme-card-border)' }}>
        <ListPagePagination
          currentPage={page}
          totalPages={Math.ceil(data.total / data.limit)}
          totalItems={data.total}
          pageSize={data.limit}
          onPageChange={onPageChange}
          itemLabel="registros"
          data-testid="analytics-visits-entries-pagination"
        />
      </div>
    )}
  </DashCard>
);

VisitsEntriesCard.displayName = 'VisitsEntriesCard';
